import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { FaCheck } from "react-icons/fa";
import DragAndDropImage from "../DragAndDropImage";
import { createQuestion, updateQuestion } from "../../api/custom-mode";
import { getPointColor } from "../../utils/pointColor";
import { Button, Field, Modal, Spinner, Textarea } from "../../ui";

const POINTS = [100, 200, 300, 400, 500];

// Add or edit a single question. `question` is the existing doc (edit) or null (add).
const QuestionEditor = ({ modeId, question, onClose, onSaved }) => {
  const isEdit = !!question;
  const [questionText, setQuestionText] = useState(question?.question || "");
  const [answerText, setAnswerText] = useState(question?.answer || "");
  const [points, setPoints] = useState(question?.points || 100);
  const [questionImage, setQuestionImage] = useState(null);
  const [answerImage, setAnswerImage] = useState(null);

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      const info = {
        question: questionText.trim(),
        answer: answerText.trim(),
        points,
      };
      if (questionImage) info.questionImage = questionImage;
      if (answerImage) info.answerImage = answerImage;
      return isEdit
        ? updateQuestion(question._id, info)
        : createQuestion(modeId, info);
    },
    onSuccess: () => {
      toast.success(isEdit ? "تم تعديل السؤال" : "تمت إضافة السؤال");
      onSaved?.();
    },
    onError: (e) =>
      toast.error(e?.response?.data?.message || "تعذّر حفظ السؤال"),
  });

  const handleSave = () => {
    if (!questionText.trim() || !answerText.trim()) {
      toast.error("السؤال والإجابة مطلوبان");
      return;
    }
    mutate();
  };

  return (
    <Modal open onClose={() => !isPending && onClose()} title="مصنع الأسئلة" size="lg">
      <div className="space-y-4">
        <Field label="السؤال" htmlFor="question-text">
          <Textarea
            id="question-text"
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            rows={2}
            className="resize-none"
          />
        </Field>

        <Field label="الإجابة" htmlFor="answer-text">
          <Textarea
            id="answer-text"
            value={answerText}
            onChange={(e) => setAnswerText(e.target.value)}
            rows={2}
            className="resize-none"
          />
        </Field>

        <Field label="النقاط">
          <div className="flex gap-2" role="radiogroup" aria-label="النقاط">
            {POINTS.map((p) => (
              <button
                key={p}
                type="button"
                role="radio"
                aria-checked={points === p}
                onClick={() => setPoints(p)}
                className={`flex-1 h-11 rounded-xl font-bold text-white transition-all ${
                  points === p
                    ? "ring-2 ring-white ring-offset-2 ring-offset-header"
                    : "opacity-50 hover:opacity-80"
                }`}
                style={{ backgroundColor: getPointColor(p) }}
              >
                {p}
              </button>
            ))}
          </div>
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="صورة السؤال">
            <DragAndDropImage
              onFileSelected={setQuestionImage}
              initialPreview={question?.questionImage || ""}
              label="صورة السؤال"
            />
          </Field>
          <Field label="صورة الإجابة">
            <DragAndDropImage
              onFileSelected={setAnswerImage}
              initialPreview={question?.answerImage || ""}
              label="صورة الإجابة"
            />
          </Field>
        </div>

        <div className="flex gap-3">
          <Button
            className="flex-1"
            onClick={handleSave}
            disabled={isPending}
            icon={isPending ? <Spinner /> : <FaCheck />}
          >
            حفظ
          </Button>
          <Button variant="secondary" className="flex-1" onClick={onClose} disabled={isPending}>
            إلغاء
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default QuestionEditor;
